import React, { useState, useEffect, useRef } from "react";
import { Box, Skeleton } from "@mui/joy";
import { useAxios } from "../../hooks";

interface AuthenticatedImageProps {
    assetId?: string | null;
    url?: string | null; // Fallback or direct URL if not an assetId
    alt?: string;
    sx?: any;
    className?: string;
    onLoad?: () => void;
    onClick?: () => void;
    borderRadius?: string | number;
    width?: string | number;
    height?: string | number;
    objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down";
    isBackground?: boolean;
    suppressLoadingUI?: boolean;
    children?: React.ReactNode; // For background image usage
}

export function AuthenticatedImage({
    assetId,
    url,
    alt = "",
    sx,
    className,
    onLoad,
    onClick,
    borderRadius = "md",
    width = "100%",
    height = "100%",
    objectFit = "cover",
    isBackground = false,
    suppressLoadingUI = false,
    children,
}: AuthenticatedImageProps) {
    const [imgSrc, setImgSrc] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);
    const hasLoadedRef = useRef(false);
    const { axiosPrivate } = useAxios();

    useEffect(() => {
        hasLoadedRef.current = false;
    }, [assetId, url]);

    useEffect(() => {
        let isMounted = true;
        let blobUrl: string | null = null;

        const loadContent = async () => {
            // Mode 1: Direct URL (like base64 or public link)
            if (url && !assetId) {
                setLoading(true);
                setError(false);
                setImgSrc(url);
                return;
            }

            // Mode 2: AssetId (Requires authenticated fetch)
            if (assetId) {
                try {
                    setLoading(true);
                    setError(false);
                    setImgSrc(null);

                    // Rely on axiosPrivate interceptors for the token
                    const response = await axiosPrivate.get(`/v1/asset/download`, {
                        params: { AssetId: assetId },
                        responseType: "blob",
                    });

                    if (isMounted) {
                        blobUrl = URL.createObjectURL(response.data);
                        setImgSrc(blobUrl);
                    }
                } catch (err) {
                    console.error("AuthenticatedImage error:", err);
                    if (isMounted) {
                        setError(true);
                        setLoading(false);
                    }
                }
            } else {
                setLoading(false);
                setImgSrc(null);
            }
        };

        loadContent();

        return () => {
            isMounted = false;
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
            }
        };
    }, [assetId, url]);

    const handleImageLoad = () => {
        if (hasLoadedRef.current) return;
        hasLoadedRef.current = true;
        setLoading(false);
        onLoad?.();
    };

    useEffect(() => {
        if (imgRef.current?.complete && imgSrc) {
            handleImageLoad();
        }
    }, [imgSrc]);

    if (loading && !imgSrc) {
        if (suppressLoadingUI) {
            return null;
        }

        return (
            <Box
                sx={{
                    width,
                    height,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "background.level1",
                    borderRadius,
                    ...sx,
                }}
            >
                <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    sx={{ borderRadius }}
                />
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    width,
                    height,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "background.level2",
                    borderRadius,
                    border: "1px solid",
                    borderColor: "divider",
                    ...sx,
                }}
            >
                <Skeleton variant="rectangular" width="100%" height="100%" />
            </Box>
        );
    }

    if (!imgSrc) {
        return null;
    }

    if (isBackground) {
        return (
            <Box
                className={className}
                onClick={onClick}
                sx={{
                    width,
                    height,
                    borderRadius,
                    backgroundImage: `url(${imgSrc})`,
                    backgroundSize: objectFit,
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    position: "relative",
                    ...sx,
                }}
            >
                {children}
            </Box>
        );
    }

    return (
        <img
            ref={imgRef}
            src={imgSrc}
            alt={alt}
            className={className}
            style={{
                width: typeof width === "number" ? `${width}px` : width,
                height: typeof height === "number" ? `${height}px` : height,
                borderRadius: typeof borderRadius === "number" ? `${borderRadius}px` : undefined,
                objectFit,
                cursor: onClick ? "pointer" : "default",
            }}
            onClick={onClick}
            onLoad={handleImageLoad}
        />
    );
}

export default AuthenticatedImage;
